/** @jsx jsx */

import React, { useState } from "react";
import { Button, Typography } from "antd";
import Recaptcha from "react-google-recaptcha";
import { Formik, ErrorMessage, Form } from "formik";
import { jsx } from "@emotion/core";

//components
import CustomButton from "../../components/button";
import AuthLayout from "../../components/authLayout";
import {
  FormCheckBox,
  FormInput,
  FormDropdown,
  FormMultiInputTags
} from "../../components/formHelpers";
import OTP from "../../components/OTP/OTP";

//hooks
import useRedirect from "../../helperHooks/useRedirect";
import useRegistration from "../../hooks/registration/useRegistration";
import useLoggedInToken from "../../helperHooks/useLoggedIn";

import { GstinValidityService } from "../../services/gstinValidityService";
import { AppSettings } from "../../AppSettings";
import { RegistrationStyles } from "./RegistrationStyles";
import { RegistrationRequestPayload } from "../../services/registrationService/RegistrationService.type";
import { MultiTags } from "../../components/formHelpers/FormMultiInputTags";
import {
  RegisterContents as CONSTANTS,
  RegistrationValidationSchema,
  GenderType,
  genderOptions
} from "./RegistrationConstants";

const { Text } = Typography;

type InitialValues = RegistrationRequestPayload & {
  isCaptchaVerified: boolean;
  isPhoneVerified: boolean;
  terms: boolean;
  gstinValidate: string;
  gstinValidityList: MultiTags[];
};

const initialState: InitialValues = {
  name: "",
  gender: GenderType.male,
  gstinValidityList: [],
  gstinValidate: "",
  phoneNumber: "",
  email: "",
  isPhoneVerified: false,
  isCaptchaVerified: false,
  terms: false
};

const Registration: React.FC = () => {
  const redirect = useRedirect();
  const {
    registration,
    setRegistrationIssuePhoneNumber,
    registrationState: { loading }
  } = useRegistration();

  const [termsUrl, setTermsUrl] = useState<string>();
  const recaptchaDeveloperKey = AppSettings.server.recaptchaKey;
  const loggedIn = useLoggedInToken();

  const onGenerateSuccess = (termsAndCondUrl?: string) => {
    if (termsAndCondUrl) {
      setTermsUrl(termsAndCondUrl);
    }
  };

  const handleReportIssueClick = (phoneNumber: string) => {
    setRegistrationIssuePhoneNumber(phoneNumber);
    redirect(routes => routes.reportIssue.new);
  };

  const handleSubmitFunction = (values: InitialValues) => {
    const { gstinValidityList, name, gender, phoneNumber, email } = values;
    const gstins = gstinValidityList
      .filter(item => !item.error && !item.removed)
      .map(item => item.value);

    const registerPayload = {
      name,
      gender,
      phoneNumber,
      email
    };

    const registeredGstins = gstins
      .filter((item: string, index: number) => gstins.indexOf(item) === index)
      .map(item => {
        return { gstin: item, isActive: true };
      });
    registration({
      data: {
        ...registerPayload,
        phoneNumber: registerPayload.phoneNumber,
        registeredGstins
      },
      redirect: redirectToLogIn
    });
  };

  const redirectToLogIn = () => redirect(routes => routes.login.new);

  const renderForm = (
    <AuthLayout>
      <div css={RegistrationStyles} className="py-4">
        <Text className="title">
          {CONSTANTS.labels.heading}
          <span className="title-underline d-block" />
        </Text>
        <Formik
          initialValues={initialState}
          onSubmit={handleSubmitFunction}
          validationSchema={RegistrationValidationSchema}
          validateOnChange
          validateOnBlur
        >
          {({ handleChange, values, errors, setFieldValue, resetForm }) => (
            <Form>
              <OTP
                className="mt-4"
                phoneNumber={values.phoneNumber}
                label={CONSTANTS.labels.phoneNumber}
                placeholder={CONSTANTS.placeholders.phoneNumber}
                handleChange={handleChange}
                onReset={() => {
                  resetForm();
                }}
                isValid={!!errors.phoneNumber}
                register={true}
                onGenerateSuccess={onGenerateSuccess}
                onVerifySuccess={() => {
                  setFieldValue("isPhoneVerified", true);
                }}
              />
              {values.isPhoneVerified && (
                <React.Fragment>
                  <section className="d-flex mt-4">
                    <FormDropdown
                      dropdownMatchSelectWidth={false}
                      name="gender"
                      className="flex-grow-1"
                      label={CONSTANTS.labels.gender}
                      options={genderOptions}
                      mandatory
                    />
                    <FormInput
                      name="name"
                      className="name-field ml-3"
                      label={CONSTANTS.labels.name}
                      placeholder={CONSTANTS.placeholders.name}
                      mandatory
                    />
                  </section>
                  <FormInput
                    className="mt-4"
                    name="email"
                    label={CONSTANTS.labels.email}
                    placeholder={CONSTANTS.placeholders.email}
                    mandatory
                  />
                  <FormMultiInputTags
                    className="mt-4"
                    name="gstinValidate"
                    label={CONSTANTS.labels.gstin}
                    placeholder={CONSTANTS.placeholders.gstin}
                    listName="gstinValidityList"
                    onAdd={GstinValidityService.checkGstinRegisterable}
                    listItems={values.gstinValidityList}
                    mandatory
                  />
                  {!errors.gstinValidate && (
                    <ErrorMessage name="gstinValidityList">
                      {errorMessage => (
                        <Typography className="error">
                          {errorMessage}
                        </Typography>
                      )}
                    </ErrorMessage>
                  )}
                  <FormCheckBox
                    name="terms"
                    className="mb-4"
                    label={
                      <span>
                        {CONSTANTS.labels.understandTerms}
                        <a
                          className="anchor-link"
                          href={termsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {CONSTANTS.labels.terms}
                        </a>
                      </span>
                    }
                  />
                  <Recaptcha
                    sitekey={recaptchaDeveloperKey}
                    onChange={() => {
                      setFieldValue("isCaptchaVerified", true);
                    }}
                  />
                  <ErrorMessage name="isCaptchaVerified">
                    {msg => <Text className="error d-block">{msg}</Text>}
                  </ErrorMessage>
                  <CustomButton
                    htmlType="submit"
                    containerDivClassName="justify-content-center mt-4"
                    type="primary"
                    disabled={loading}
                    loading={loading}
                  >
                    {CONSTANTS.buttons.register}
                  </CustomButton>
                </React.Fragment>
              )}
              <div className="d-flex justify-content-between mt-4">
                <Text>
                  {CONSTANTS.labels.alreadyRegistered}
                  <Button
                    type="link"
                    className="pl-0"
                    onClick={redirectToLogIn}
                  >
                    {CONSTANTS.buttons.logIn}
                  </Button>
                </Text>
                <Button
                  className="px-0"
                  type="link"
                  onClick={() => handleReportIssueClick(values.phoneNumber)}
                >
                  {CONSTANTS.labels.reportIssue}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
  return !loggedIn ? (
    renderForm
  ) : (
    <div>{CONSTANTS.feedbacks.registrationNoAccess}</div>
  );
};

export default Registration;
